import { useMediaQuery } from '@mui/material'
import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useOktaAuth } from '@okta/okta-react'
import { SurveyResponse, Survey } from '../../types/survey'
import actionsService from '../../services/actionsService'
import { isMobileDown } from '../../theme/deviceChecks'
import { showSuccessMessage, showErrorMessage } from '../../redux/reducers/snackbarReducer'

export function useSurveyModal() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pendingSurveys, setPendingSurveys] = useState<Survey[]>([])
  const { authState } = useOktaAuth()
  const dispatch = useDispatch()

  const isMobile = useMediaQuery(isMobileDown)
  
  useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      return
    }

    const fetchPendingSurveys = async () => {
      try {
        const response = await actionsService.userAnalytics.getPendingSurveys()
        setPendingSurveys(response.surveys)
      } catch (error) {
        // Surveys are not critical to the user journeys, so ignore the errors
      }
    }

    fetchPendingSurveys()
  }, [authState?.isAuthenticated])

  const showSurvey = !isMobile && pendingSurveys.length > 0

  const handleSurveySubmit = useCallback(async (response: SurveyResponse) => {
    if (pendingSurveys.length === 0) return
    
    setIsSubmitting(true)
    
    try {
      await actionsService.userAnalytics.postSurveyResponse(
        pendingSurveys[0].id,
        response.response,
        response.comments
      )
      // For now we'll only show max 1 survey on app load, so remove all pending surveys on submission.
      // If we want to show multiple surveys in a row, we'll need to add extra logic to reset user's responses.
      setPendingSurveys([])
      dispatch(showSuccessMessage('Thank you for your feedback!'))
    } catch (error) {
      dispatch(showErrorMessage('Failed to submit survey. Please try again later.'))
    } finally {
      setIsSubmitting(false)
    }
  }, [pendingSurveys, dispatch])

  const handleSurveyClose = useCallback(async () => {
    if (pendingSurveys.length === 0) return

    try {
      // Submit survey response with null response and empty comments when modal is closed
      await actionsService.userAnalytics.postSurveyResponse(
        pendingSurveys[0].id,
        null,
        ''
      )
    } catch (error) {
      // This submission happens in the background, so showing an error message to the user
      // would not be particularly useful
    }
    
    setPendingSurveys([])
  }, [pendingSurveys])

  return {
    showSurvey,
    isSubmitting,
    question: pendingSurveys[0]?.question,
    handleSurveySubmit,
    handleSurveyClose,
  }
}